import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Heart, TrendingUp, Sun, Droplet, Eye,
  ArrowRight, Quote, Star,
} from "lucide-react";
import { Particles } from "@/components/site/Particles";
import { SacredGeometry } from "@/components/site/SacredGeometry";
import { Reveal } from "@/components/site/Reveal";
import { IMG } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const ICONS = { Heart, Sparkles, TrendingUp, Sun, Droplet, Eye };
const resolveImg = (v) => (v && typeof v === "string" && v.startsWith("http")) ? v : (IMG[v] || v);

const Hero = ({ content }) => {
  const h = content.hero || {};
  const stats = content.stats || [];
  const brand = content.brand || {};
  return (
    <section data-testid="home-hero" className="relative min-h-[100vh] flex items-center overflow-hidden hero-radial">
      <div className="absolute inset-0 -z-10">
        <img src={resolveImg(h.image) || IMG.hero} alt="" className="h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/85 via-midnight/75 to-midnight" />
      </div>
      <div className="cosmic-blob bg-cosmic-light h-[420px] w-[420px] -top-32 -left-32" />
      <div className="cosmic-blob bg-saffron h-[380px] w-[380px] bottom-0 right-0 opacity-20" />
      <Particles count={36} />
      <div className="pointer-events-none absolute right-[-220px] top-[-120px] h-[820px] w-[820px] opacity-50">
        <SacredGeometry className="h-full w-full spin-slow" strokeOpacity={0.45} />
      </div>
      <div className="wrap relative z-10 grid gap-10 pt-32 pb-20">
        <Reveal><div className="eyebrow">{h.eyebrow}</div></Reveal>
        <Reveal delay={120}>
          <h1 className="display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-ivory max-w-5xl">
            {h.headlineLine1}<br className="hidden md:block" />
            <em>{h.headlineLine2}</em>
          </h1>
        </Reveal>
        <Reveal delay={240}>
          <p className="max-w-2xl text-lg md:text-xl text-ivory/75 leading-relaxed">
            {brand.tagline && <>{brand.tagline}. </>}{h.subtitle}
          </p>
        </Reveal>
        <Reveal delay={360}>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="btn-gold" data-testid="hero-cta-book">
              {h.primaryCtaText || "Book a Consultation"} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/services" className="btn-ghost" data-testid="hero-cta-process">
              {h.secondaryCtaText || "Explore the Process"}
            </Link>
          </div>
        </Reveal>
        <Reveal delay={480}>
          <div className="mt-10 flex items-center gap-6 text-ivory/55 text-sm">
            <div className="flex items-center gap-1 text-gold">
              {[...Array(5)].map((_, i) => (<Star key={i} className="h-4 w-4 fill-gold" strokeWidth={0} />))}
            </div>
            <div className="h-4 w-px bg-gold/30" />
            <span className="tracking-widest uppercase text-xs">
              Trusted by seekers in {stats?.[3]?.n || 14} countries
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const WhatIs = ({ content }) => {
  const w = content.whatIs || {};
  const stats = content.stats || [];
  return (
    <section className="section relative" data-testid="home-what-is">
      <div className="wrap grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="eyebrow mb-5">{w.eyebrow}</div>
          <h2 className="display text-4xl md:text-5xl text-ivory">{w.title}</h2>
          <p className="mt-6 text-ivory/70 leading-relaxed">{w.body1}</p>
          <p className="mt-4 text-ivory/70 leading-relaxed">{w.body2}</p>
          <div className="mt-8 grid grid-cols-2 gap-6 max-w-md">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="font-display text-3xl text-gold">{s.n}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-ivory/50 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={150}>
          <div className="relative">
            <div className="glass p-2 ring-gold">
              <img src={resolveImg(w.image) || IMG.sacredGeometry} alt="" className="aspect-[4/5] w-full object-cover" />
            </div>
            {w.quote && (
              <div className="absolute -bottom-8 -left-8 glass-strong p-6 max-w-[240px]">
                <Quote className="h-5 w-5 text-gold" strokeWidth={1.5} />
                <p className="mt-3 font-display text-lg text-ivory/90 italic leading-snug">"{w.quote}"</p>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const HowItWorks = ({ content }) => {
  const steps = content.howItWorks || [];
  const s = content.sections || {};
  return (
    <section id="process" data-testid="home-how-it-works" className="section relative border-t border-gold/10 bg-midnight-paper/40">
      <div className="cosmic-blob bg-cosmic h-[400px] w-[400px] top-20 left-1/2 -translate-x-1/2 opacity-20" />
      <div className="wrap relative">
        <Reveal>
          <div className="eyebrow mb-5">{s.howItWorksEyebrow}</div>
          <h2 className="display text-4xl md:text-5xl text-ivory max-w-3xl">{s.howItWorksTitle}</h2>
        </Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-5">
          {steps.map((step, i) => (
            <Reveal key={step.n || i} delay={i * 80}>
              <div className="glass lift p-6 h-full">
                <div className="font-display text-5xl text-gold/40">{step.n}</div>
                <div className="gold-divider my-4" />
                <h3 className="font-display text-2xl text-ivory">{step.title}</h3>
                <p className="mt-3 text-ivory/65 text-sm leading-relaxed">{step.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const BrahmaSatyaBlock = ({ content }) => {
  const b = content.brahmaSatyaHome || {};
  return (
    <section data-testid="home-brahma-satya" className="section relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={IMG.cosmos} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-midnight via-midnight/85 to-midnight/40" />
      </div>
      <div className="wrap grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="eyebrow mb-5">{b.eyebrow}</div>
          <h2 className="display text-4xl md:text-5xl text-ivory">{b.title}</h2>
          <p className="mt-6 text-ivory/70 leading-relaxed">{b.body}</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {(b.items || []).map((x, i) => (
              <div key={i} className="glass p-5 lift">
                <h4 className="font-display text-xl text-gold">{x.t}</h4>
                <p className="mt-2 text-sm text-ivory/65 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
          <Link to="/services" className="btn-ghost mt-8" data-testid="home-brahma-cta">
            {b.ctaText || "Explore Brahma Satya"} <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <Reveal delay={150}>
          <div className="relative aspect-square w-full max-w-[520px] mx-auto">
            <div className="absolute inset-0 spin-slow opacity-70">
              <SacredGeometry className="h-full w-full" strokeOpacity={0.55} />
            </div>
            <div className="absolute inset-12 rounded-full bg-gradient-to-br from-cosmic/40 via-transparent to-gold/20 blur-2xl" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-cinzel text-2xl tracking-[0.5em] text-gold">
                ब्रह्म<span className="text-ivory">·</span>सत्य
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Benefits = ({ content }) => {
  const list = content.benefits || [];
  const s = content.sections || {};
  return (
    <section className="section relative" data-testid="home-benefits">
      <div className="wrap">
        <Reveal>
          <div className="eyebrow mb-5">{s.benefitsEyebrow}</div>
          <h2 className="display text-4xl md:text-5xl text-ivory max-w-3xl">{s.benefitsTitle}</h2>
        </Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((b, i) => {
            const Icon = ICONS[b.icon] || Sparkles;
            return (
              <Reveal key={b.title + i} delay={i * 60}>
                <div className="glass lift p-7 h-full">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 bg-gold/5">
                    <Icon className="h-5 w-5 text-gold" strokeWidth={1.4} />
                  </div>
                  <h3 className="mt-6 font-display text-2xl text-ivory">{b.title}</h3>
                  <p className="mt-3 text-ivory/65 leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Testimonials = ({ content }) => {
  const list = (content.testimonials || [])
    .filter((t) => t.visible !== false && (t.featured === true || t.featured === "true"))
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
  const s = content.sections || {};
  return (
    <section data-testid="home-testimonials" className="section relative border-y border-gold/10 bg-midnight-paper/40">
      <div className="wrap">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="eyebrow mb-5">{s.testimonialsEyebrow}</div>
              <h2 className="display text-4xl md:text-5xl text-ivory max-w-3xl">{s.testimonialsTitle}</h2>
            </div>
            <Link to="/testimonials" className="btn-ghost text-[10px]" data-testid="home-testimonials-cta">
              All Stories <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {list.map((t, i) => (
            <Reveal key={t.name + i} delay={i * 80}>
              <article className="glass lift p-8 h-full relative">
                <Quote className="absolute right-6 top-6 h-12 w-12 text-gold/15" strokeWidth={1} />
                {t.rating && (
                  <div className="flex items-center gap-0.5 text-gold mb-3">
                    {[...Array(5)].map((_, k) => (<Star key={k} className={`h-3.5 w-3.5 ${k < (Number(t.rating) || 5) ? "fill-gold" : "opacity-30"}`} strokeWidth={0} />))}
                  </div>
                )}
                <p className="font-display italic text-2xl leading-snug text-ivory/90">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-4">
                  {t.avatar ? (
                    <img src={resolveImg(t.avatar) || IMG.avatar1} alt={t.name} className="h-12 w-12 rounded-full object-cover ring-1 ring-gold/40" />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold font-display text-lg">
                      {(t.name || "?").charAt(0)}
                    </div>
                  )}
                  <div>
                    <div className="text-ivory">{t.name}</div>
                    <div className="text-xs uppercase tracking-[0.2em] text-ivory/50">
                      {[t.service, t.role, t.country].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Founder = ({ content }) => {
  const f = content.founder || {};
  return (
    <section className="section relative" data-testid="home-founder">
      <div className="wrap grid items-center gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <div className="relative">
            <div className="glass p-2 ring-gold">
              <img src={resolveImg(f.image) || IMG.founder} alt="The Founder" className="aspect-[3/4] w-full object-cover" />
            </div>
            {f.badge && (
              <div className="absolute -right-4 -bottom-4 bg-gold text-midnight px-5 py-3 font-display text-lg">
                {f.badge}
              </div>
            )}
          </div>
        </Reveal>
        <Reveal delay={150} className="lg:col-span-7">
          <div className="eyebrow mb-5">{f.eyebrow}</div>
          <h2 className="display text-4xl md:text-5xl text-ivory">"{f.title}"</h2>
          <p className="mt-6 text-ivory/70 leading-relaxed">{f.body1}</p>
          <p className="mt-4 text-ivory/70 leading-relaxed">{f.body2}</p>
          <Link to="/services" className="btn-ghost mt-8" data-testid="home-founder-cta">
            {f.ctaText || "The Founder's Journey"} <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const FAQBlock = ({ content }) => {
  const faqs = content.faqs || [];
  const s = content.sections || {};
  return (
    <section className="section relative" data-testid="home-faq">
      <div className="wrap max-w-3xl">
        <Reveal>
          <div className="eyebrow mb-5 text-center">{s.faqEyebrow}</div>
          <h2 className="display text-4xl md:text-5xl text-ivory text-center">{s.faqTitle}</h2>
        </Reveal>
        <Reveal delay={150}>
          <Accordion type="single" collapsible className="mt-12">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q + i} value={`faq-${i}`} className="border-b border-gold/15">
                <AccordionTrigger data-testid={`home-faq-trigger-${i}`} className="text-left font-display text-xl md:text-2xl text-ivory hover:text-gold py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ivory/70 leading-relaxed text-base pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

const ContactBlock = ({ content }) => {
  const brand = content.brand || {};
  const s = content.sections || {};
  const number = (brand.whatsappNumber || "+910000000000").replace(/\D/g, "");
  const msg = brand.whatsappMessage || "Namaste";
  const wa = `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
  return (
    <section data-testid="home-contact" className="section relative overflow-hidden border-t border-gold/15">
      <div className="cosmic-blob bg-cosmic h-[500px] w-[500px] -top-32 -left-32" />
      <div className="cosmic-blob bg-saffron h-[400px] w-[400px] bottom-0 right-0 opacity-25" />
      <div className="wrap relative grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="eyebrow mb-5">{s.contactEyebrow}</div>
          <h2 className="display text-4xl md:text-5xl text-ivory">{s.contactTitle}</h2>
          <p className="mt-6 text-ivory/70 leading-relaxed max-w-lg">{s.contactBody}</p>
        </Reveal>
        <Reveal delay={150}>
          <div className="glass-strong p-8">
            <h3 className="font-display text-2xl text-ivory">Connect on WhatsApp</h3>
            <p className="mt-2 text-ivory/65 text-sm">
              Tap below to start a private conversation. We typically respond within a few hours.
            </p>
            <a href={wa} target="_blank" rel="noreferrer" className="btn-gold mt-6 w-full justify-center" data-testid="home-whatsapp-cta">
              Chat on WhatsApp
            </a>
            <div className="gold-divider my-6" />
            <div className="grid gap-2 text-sm text-ivory/65">
              <div>Email · <a className="text-gold" href={`mailto:${brand.email}`}>{brand.email}</a></div>
              <div>Based in · {brand.city}</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Highlight = ({ content }) => {
  const h = content.highlight || {};
  if (h.visible === false) return null;
  return (
    <section data-testid="home-highlight" className="relative py-14 border-y border-gold/15 overflow-hidden">
      <div className="cosmic-blob bg-cosmic h-[280px] w-[280px] -top-24 -left-16 opacity-30" />
      <div className="cosmic-blob bg-saffron h-[240px] w-[240px] -bottom-16 -right-10 opacity-20" />
      <div className="wrap relative grid items-center gap-8 lg:grid-cols-12">
        {h.image && (
          <div className="lg:col-span-4">
            <div className="glass p-1.5 ring-gold aspect-[4/3] lg:aspect-square">
              <img src={h.image} alt="" className="h-full w-full object-cover" />
            </div>
          </div>
        )}
        <div className={h.image ? "lg:col-span-8" : "lg:col-span-12 text-center"}>
          <div className="eyebrow mb-3">{h.eyebrow || h.kind || "Thought of the Day"}</div>
          <h3 className="display text-3xl md:text-4xl text-ivory">
            <span className="shimmer-text">"{h.title}"</span>
          </h3>
          {h.body && <p className="mt-4 text-ivory/70 leading-relaxed max-w-2xl">{h.body}</p>}
          {h.ctaText && h.ctaLink && (
            <a href={h.ctaLink} target={h.ctaLink.startsWith("http") ? "_blank" : "_self"} rel="noreferrer" className="btn-ghost mt-6">
              {h.ctaText} <ArrowRight className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const { content } = useContent();
  return (
    <div data-testid="home-page">
      <Hero content={content} />
      <Highlight content={content} />
      <WhatIs content={content} />
      <HowItWorks content={content} />
      <BrahmaSatyaBlock content={content} />
      <Benefits content={content} />
      <Testimonials content={content} />
      <Founder content={content} />
      <FAQBlock content={content} />
      <ContactBlock content={content} />
    </div>
  );
};

export default Home;
